import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { RefreshCw } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

interface PageHeaderProps {
  /** Page title */
  title: string
  /** Page description/subtitle */
  description?: string
  /** Icon component to display */
  icon?: LucideIcon
  /** Custom icon element (alternative to icon prop) */
  iconElement?: ReactNode
  /** Whether data is currently loading/refreshing */
  loading?: boolean
  /** Callback for refresh button click */
  onRefresh?: () => void
  /** Text to show on refresh button (e.g., "Updated 5m ago") */
  refreshLabel?: string
  /** Additional actions to render (buttons, etc.) */
  actions?: ReactNode
  /** Additional className for the container */
  className?: string
  /** Whether to show the bottom border */
  showBorder?: boolean
}

/**
 * Reusable responsive page header component.
 * Handles mobile/desktop layout automatically.
 */
export function PageHeader({
  title,
  description,
  icon: Icon,
  iconElement,
  loading,
  onRefresh,
  refreshLabel,
  actions,
  className,
  showBorder = true,
}: PageHeaderProps) {
  return (
    <div className={cn('pb-4 sm:pb-6', showBorder && 'border-b border-border/50', className)}>
      <div className="flex items-start justify-between gap-3 sm:items-center">
        {/* Left side: Icon + Title */}
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          {/* Icon container */}
          {(Icon || iconElement) && (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 sm:h-12 sm:w-12">
              {iconElement || (Icon && <Icon className="h-5 w-5 text-primary sm:h-6 sm:w-6" />)}
            </div>
          )}

          {/* Title and description */}
          <div className="min-w-0">
            <h1 className="text-xl font-bold tracking-tight text-foreground sm:text-3xl">
              {title}
            </h1>
            {description && (
              <p className="mt-0.5 line-clamp-1 hidden text-xs text-muted-foreground sm:block sm:text-sm">
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Right side: Actions */}
        <div className="flex shrink-0 items-center gap-2">
          {/* Custom actions */}
          {actions}

          {/* Refresh button */}
          {onRefresh && (
            <Button
              variant="outline"
              size="icon"
              onClick={onRefresh}
              disabled={loading}
              className="h-9 w-9 sm:h-9 sm:w-auto sm:gap-2 sm:px-3"
            >
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
              <span className="hidden sm:inline">{refreshLabel || 'Refresh'}</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
